import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  useMap,
  FeatureGroup,
  Popup,
  Polygon,
} from "react-leaflet";
import L from "leaflet";
import { EditControl } from "react-leaflet-draw";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "../styles/TrekMotor.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const API_URL = "https://depotaircerdas.com/api/tracker";

const TrekMotor = () => {
  const [auth, setAuth] = useState({ token: null, user: null, isSuperAdmin: false });
  const [loadingAuth, setLoadingAuth] = useState(true);

  const [state, setState] = useState({
    lastPosition: null,
    gpsTrail: [],
    deviceStatus: "Offline",
    lastSeen: "",
    isLiveTracking: true,
    historyData: null,
    startDate: "",
    endDate: "",
    totalDistance: 0,
    avgSpeed: 0,
    totalDuration: "0",
    isBarVisible: true,
    geofences: [],
    fuelEfficiency: 10,
    error: "",
    loadingHistory: false,
  });

  const drawnItemsRef = useRef(new L.FeatureGroup());
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUsername = localStorage.getItem("username");
    const isAdmin = localStorage.getItem("is_super_admin") === "true";

    if (storedToken && storedUsername) {
      setAuth({ token: storedToken, user: storedUsername, isSuperAdmin: isAdmin });
    }
    setLoadingAuth(false);
  }, []);

  const fetchData = useCallback(
    async (url) => {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });

      if (response.status === 404) return { success: true, data: null };
      if (!response.ok) throw new Error(response.statusText);
      return response.json();
    },
    [auth.token]
  );

  const fetchLastPosition = useCallback(() => {
    fetchData(`${API_URL}/last-position/${auth.user}`)
      .then((result) => {
        if (result.success && result.data) {
          const { latitude, longitude, timestamp } = result.data;
          setState((s) => ({
            ...s,
            lastPosition: [latitude, longitude],
            lastSeen: new Date(timestamp).toLocaleString("id-ID"),
            deviceStatus: "Online",
            gpsTrail: [...s.gpsTrail, [latitude, longitude]].slice(-50),
          }));
        } else {
          setState((s) => ({ ...s, deviceStatus: "Offline" }));
        }
      })
      .catch(() => setState((s) => ({ ...s, deviceStatus: "Offline" })));
  }, [auth.user, fetchData]);

  const fetchDeviceStatus = useCallback(() => {
    fetchData(`${API_URL}/device-status/${auth.user}`)
      .then((result) => {
        if (result.success && result.data) {
          const { status, last_seen } = result.data;
          setState((s) => ({
            ...s,
            deviceStatus: status,
            lastSeen: last_seen ? new Date(last_seen).toLocaleString("id-ID") : "-",
          }));
        }
      })
      .catch(() => null);
  }, [auth.user, fetchData]);

  const fetchAndDisplayGeofence = useCallback(() => {
    fetchData(`${API_URL}/geofence/${auth.user}`)
      .then((result) => {
        if (result.success && result.data) {
          setState((s) => ({
            ...s,
            geofences: result.data.map((g) => ({
              ...g,
              coordinates: JSON.parse(g.coordinates).map((c) => L.latLng(c[0], c[1])),
            })),
          }));
        } else {
          setState((s) => ({ ...s, geofences: [] }));
        }
      })
      .catch(() => setState((s) => ({ ...s, geofences: [] })));
  }, [auth.user, fetchData]);

  const formatDuration = (seconds) => {
    const total = Number(seconds || 0);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = Math.floor(total % 60);
    return `${h} jam ${m} menit ${s} detik`;
  };

  const getHistory = useCallback(async () => {
    if (!state.startDate || !state.endDate) {
      setState((s) => ({ ...s, error: "Harap isi tanggal awal dan akhir." }));
      return;
    }

    setState((s) => ({
      ...s,
      isLiveTracking: false,
      loadingHistory: true,
      error: "",
    }));

    try {
      const [historyRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/history/${auth.user}?start_date=${state.startDate}&end_date=${state.endDate}`, {
          headers: { Authorization: `Bearer ${auth.token}` },
        }),
        fetch(`${API_URL}/summary/${auth.user}?start_date=${state.startDate}&end_date=${state.endDate}`, {
          headers: { Authorization: `Bearer ${auth.token}` },
        }),
      ]);

      if (!historyRes.ok || !summaryRes.ok) {
        throw new Error("Gagal mengambil data riwayat.");
      }

      const [historyResult, summaryResult] = await Promise.all([
        historyRes.json(),
        summaryRes.json(),
      ]);

      if (historyResult.success && historyResult.data && summaryResult.success && summaryResult.data) {
        setState((s) => ({
          ...s,
          historyData: historyResult.data,
          lastPosition: null,
          gpsTrail: historyResult.data.map((p) => [p.latitude, p.longitude]),
          totalDistance: Number(summaryResult.data.total_distance_km || 0),
          avgSpeed: Number(summaryResult.data.average_speed_kmh || 0),
          totalDuration: formatDuration(summaryResult.data.total_duration_sec || 0),
          error: "",
        }));
      } else {
        setState((s) => ({
          ...s,
          historyData: null,
          gpsTrail: [],
          totalDistance: 0,
          avgSpeed: 0,
          totalDuration: "0",
          error: "Tidak ada riwayat perjalanan pada periode ini.",
        }));
      }
    } catch (error) {
      console.error("Gagal mengambil riwayat:", error);
      setState((s) => ({
        ...s,
        error: `Terjadi kesalahan: ${error.message}`,
      }));
    } finally {
      setState((s) => ({ ...s, loadingHistory: false }));
    }
  }, [auth.token, auth.user, state.startDate, state.endDate]);

  const handleDrawCreated = useCallback(
    (e) => {
      const { layer } = e;
      const geofenceName = prompt("Beri nama geofence ini:");
      if (geofenceName) {
        const coordinates = layer.getLatLngs()[0].map((latlng) => [latlng.lat, latlng.lng]);
        saveGeofence(geofenceName, coordinates);
      } else {
        e.target.removeLayer(layer);
      }
    },
    [auth.token, auth.user]
  );

  const saveGeofence = useCallback(
    async (name, coordinates) => {
      try {
        const response = await fetch(`${API_URL}/geofence`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.token}`,
          },
          body: JSON.stringify({ username: auth.user, name, coordinates }),
        });

        if (response.ok) {
          fetchAndDisplayGeofence();
        } else {
          const error = await response.json();
          setState((s) => ({
            ...s,
            error: `Gagal menyimpan geofence: ${error.message}`,
          }));
        }
      } catch (error) {
        console.error("Error saat menyimpan geofence:", error);
        setState((s) => ({
          ...s,
          error: "Terjadi kesalahan saat menyimpan geofence.",
        }));
      }
    },
    [auth.token, auth.user, fetchAndDisplayGeofence]
  );

  useEffect(() => {
    if (!state.isLiveTracking || !auth.token || !auth.user) return;
    const fetchAndUpdate = () => {
      fetchLastPosition();
      fetchDeviceStatus();
    };
    fetchAndUpdate();
    const interval = setInterval(fetchAndUpdate, 5000);
    return () => clearInterval(interval);
  }, [state.isLiveTracking, auth.token, auth.user, fetchLastPosition, fetchDeviceStatus]);

  useEffect(() => {
    if (auth.token && auth.user) fetchAndDisplayGeofence();
  }, [auth.token, auth.user, fetchAndDisplayGeofence]);

  const calculateFuel = () =>
    state.totalDistance > 0 && state.fuelEfficiency > 0
      ? (state.totalDistance / state.fuelEfficiency).toFixed(2)
      : "0";

  const chartData = useMemo(() => {
    if (!state.historyData || state.historyData.length === 0) return null;

    return {
      labels: state.historyData.map((p) =>
        new Date(p.timestamp).toLocaleTimeString("id-ID", {
          hour: "2-digit",
          minute: "2-digit",
        })
      ),
      datasets: [
        {
          label: "Kecepatan (km/h)",
          data: state.historyData.map((p) => parseFloat(p.speed || 0)),
          borderColor: "rgb(14, 165, 233)",
          backgroundColor: "rgba(14, 165, 233, 0.16)",
          tension: 0.3,
          fill: true,
        },
      ],
    };
  }, [state.historyData]);

  const MapController = () => {
    const map = useMap();

    useEffect(() => {
      mapInstanceRef.current = map;

      if (state.lastPosition && state.isLiveTracking) {
        map.panTo(state.lastPosition);
      } else if (state.gpsTrail.length > 0 && !state.isLiveTracking) {
        const bounds = L.polyline(state.gpsTrail).getBounds();
        if (bounds.isValid()) map.fitBounds(bounds);
      }
    }, [map, state.lastPosition, state.gpsTrail, state.isLiveTracking]);

    return null;
  };

  if (loadingAuth) return <div className="trek-state-box">Memuat GPS Tracker...</div>;
  if (!auth.token) return <div className="trek-state-box">Akses ditolak. Silakan login.</div>;

  return (
    <div className="page-shell">
      <div className="page-hero-card">
        <span className="page-hero-badge">Tracking Kendaraan</span>
        <h2 className="page-hero-title">Trek Motor & Lokasi Depot</h2>
        <p className="page-hero-subtitle">
          Pantau posisi terakhir kendaraan, lihat riwayat perjalanan, analisis kecepatan,
          dan kelola geofence dalam satu halaman.
        </p>
      </div>

      {state.error && <div className="trek-state-box error">{state.error}</div>}

      <div className="page-card">
        <div className="page-card-header">
          <div>
            <h3 className="section-title">Peta Pelacakan</h3>
            <p className="section-subtitle">
              Perangkat aktif: <strong>{auth.user}</strong>
            </p>
          </div>
        </div>

        <div className="trek-map-card">
          <MapContainer center={[-6.366, 106.88]} zoom={15} className="trek-map">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <MapController />
            <FeatureGroup ref={drawnItemsRef}>
              {auth.isSuperAdmin && (
                <EditControl
                  position="topright"
                  onCreated={handleDrawCreated}
                  draw={{
                    polygon: true,
                    polyline: false,
                    rectangle: false,
                    circle: false,
                    marker: false,
                    circlemarker: false,
                  }}
                />
              )}
            </FeatureGroup>

            {state.lastPosition && (
              <Marker position={state.lastPosition}>
                <Popup>
                  Posisi Terakhir
                  <br />
                  {state.lastSeen}
                </Popup>
              </Marker>
            )}

            <Polyline positions={state.gpsTrail} color="red" />

            {state.geofences.map((g, index) => (
              <Polygon key={index} positions={g.coordinates} pathOptions={{ color: "blue" }}>
                <Popup>{g.name}</Popup>
              </Polygon>
            ))}
          </MapContainer>
        </div>
      </div>

      <div className="trek-dashboard-grid">
        <section className="page-card">
          <div className="page-card-header">
            <div>
              <h3 className="section-title">Status Perangkat</h3>
              <p className="section-subtitle">Informasi live tracking dan perangkat aktif.</p>
            </div>
          </div>

          <div className="trek-stat-grid">
            <div className="trek-stat-card">
              <span>Perangkat</span>
              <strong>{auth.user}</strong>
            </div>
            <div className="trek-stat-card">
              <span>Status</span>
              <strong className={state.deviceStatus === "Online" ? "success-text" : "danger-text"}>
                {state.deviceStatus}
              </strong>
            </div>
            <div className="trek-stat-card full">
              <span>Terakhir Terlihat</span>
              <strong>{state.lastSeen || "-"}</strong>
            </div>
          </div>
        </section>

        <section className="page-card">
          <div className="page-card-header">
            <div>
              <h3 className="section-title">Filter Riwayat</h3>
              <p className="section-subtitle">Pilih rentang tanggal untuk analisis perjalanan.</p>
            </div>
          </div>

          <div className="trek-filter-grid">
            <div className="form-group">
              <label>Dari Tanggal</label>
              <input
                type="date"
                className="report-select"
                value={state.startDate}
                onChange={(e) => setState((s) => ({ ...s, startDate: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label>Hingga Tanggal</label>
              <input
                type="date"
                className="report-select"
                value={state.endDate}
                onChange={(e) => setState((s) => ({ ...s, endDate: e.target.value }))}
              />
            </div>
          </div>

          <div className="trek-action-row">
            <button className="primary-action-btn tracker-blue" onClick={getHistory}>
              {state.loadingHistory ? "Memuat..." : "Tampilkan Riwayat"}
            </button>
            <button
              className="primary-action-btn tracker-green"
              onClick={() =>
                setState((s) => ({
                  ...s,
                  isLiveTracking: true,
                  historyData: null,
                  gpsTrail: [],
                  error: "",
                }))
              }
            >
              Pelacakan Langsung
            </button>
          </div>
        </section>
      </div>

      {state.historyData && (
        <>
          <section className="page-card">
            <div className="page-card-header">
              <div>
                <h3 className="section-title">Statistik Perjalanan</h3>
                <p className="section-subtitle">Ringkasan jarak, durasi, kecepatan, dan BBM.</p>
              </div>
            </div>

            <div className="trek-stat-grid four">
              <div className="trek-stat-card">
                <span>Total Jarak</span>
                <strong>{state.totalDistance} km</strong>
              </div>
              <div className="trek-stat-card">
                <span>Durasi</span>
                <strong>{state.totalDuration}</strong>
              </div>
              <div className="trek-stat-card">
                <span>Kecepatan Rata-rata</span>
                <strong>{state.avgSpeed} km/h</strong>
              </div>
              <div className="trek-stat-card">
                <span>Perkiraan BBM</span>
                <strong>{calculateFuel()} Liter</strong>
              </div>
            </div>

            <div className="trek-fuel-box">
              <label>Efisiensi Kendaraan (km/L)</label>
              <input
                type="number"
                className="report-select"
                value={state.fuelEfficiency}
                onChange={(e) =>
                  setState((s) => ({ ...s, fuelEfficiency: parseFloat(e.target.value || 0) }))
                }
                min="1"
              />
            </div>
          </section>

          <section className="page-card">
            <div className="page-card-header">
              <div>
                <h3 className="section-title">Grafik Kecepatan</h3>
                <p className="section-subtitle">Visualisasi perubahan kecepatan sepanjang rute.</p>
              </div>
            </div>

            <div className="trek-chart-card">
              {chartData ? <Line data={chartData} /> : <div className="trek-state-box">Belum ada data grafik.</div>}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default TrekMotor;